/**
 * ContentUsersLikedContent
 *
 * Body of the owner-only "users who liked this" sheet: a like-count title, a
 * 3-column avatar grid of every user who liked the media (newest-like first),
 * a "No likes yet" empty state, and load-more pagination.
 *
 * Ports Flutter's `ContentUsersLikedBottomSheet` body
 * (`lib/presentation/bottom_sheet/content_users_liked_bottom_sheet.dart:244-345`).
 * The manual `GridWidget` becomes the shared `VirtualList` grid mode; the
 * drag-to-dismiss gesture is replaced by the `SheetService` close affordance
 * (allowed web UI deviation).
 */

import React, { useCallback, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { UserAvatarGridItem } from '@/presentation/components/common/user/UserAvatarGridItem';
import { Navigator } from '@/presentation/routing/Navigator';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import { useGridColumnsCount } from '@/presentation/hooks';
import { MathUtils } from '@/domain/utils/mathUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';
import { useContentLikedUsers } from './hooks/useContentLikedUsers';

export interface ContentUsersLikedContentProps {
  mediaPath: string;
  likes: number;
}

export const ContentUsersLikedContent: React.FC<ContentUsersLikedContentProps> = ({
  mediaPath,
  likes,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const gridColumnsCount = useGridColumnsCount();

  const { likedUsers, hasMore, isFetching, isFirstLoad, loadMore } =
    useContentLikedUsers({ mediaPath });

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Ports `appBarTitle()` (content_users_liked_bottom_sheet.dart:310-328):
  // 0 → "0 likes", 1 → "1 like", else compact count + " likes".
  const title =
    likes === 0
      ? l10n.contentUsersLiked.zeroLikes
      : likes === 1
        ? l10n.contentUsersLiked.oneLike
        : l10n.contentUsersLiked.manyLikes.replace(
            '{count}',
            MathUtils.getReactionsIntlNumber(likes),
          );

  const handleItemClick = useCallback((userId: string) => {
    // Ports `itemTapped` → `navigateToUserDetailsScreen` (lines 84-87).
    Navigator.navigateToUserDetails(userId);
  }, []);

  const renderItem = useCallback(
    (row: ContentLikedUserEntity) => (
      <UserAvatarGridItem
        userId={row.userId}
        name={row.username}
        onClick={handleItemClick}
        qa={{ id: `content-liked-user-${row.userId}` }}
      />
    ),
    [handleItemClick],
  );

  const showEmptyState = !isFirstLoad && likedUsers.length === 0;

  // Coarse lifecycle token reusing existing state: first load with no rows is
  // `loading`; settled with no rows is `empty`; a non-first page in flight is
  // `loading-more`; otherwise `ready`.
  const sheetStatus = isFirstLoad
    ? 'loading'
    : likedUsers.length === 0
      ? 'empty'
      : isFetching
        ? 'loading-more'
        : 'ready';

  return (
    <div
      {...qaAttr({ id: 'content-liked-users-sheet', status: sheetStatus })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        {...qaAttr({ id: 'content-liked-users-title', value: title })}
        style={{
          padding: responsive.spacing.lg,
          borderBottom: `${responsive.border.thin}px solid ${theme.border}`,
          textAlign: 'center',
          fontWeight: responsive.fontWeight.bold,
          color: theme.text.primary,
          fontSize: responsive.font.lg,
        }}
      >
        {title}
      </div>

      <div
        ref={scrollRef}
        className="scrollbar-hide"
        style={{
          flex: 1,
          overflowY: 'auto',
          paddingTop: responsive.spacing.base,
          paddingBottom: responsive.spacing.base,
        }}
      >
        {isFirstLoad && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: responsive.spacing.xl,
            }}
          >
            <ExpauseLoadingIndicator />
          </div>
        )}

        {showEmptyState && (
          <div
            style={{
              textAlign: 'center',
              paddingTop: responsive.spacing['3xl'],
              paddingBottom: responsive.spacing['3xl'],
              paddingLeft: responsive.spacing.lg,
              paddingRight: responsive.spacing.lg,
              color: theme.text.secondary,
              fontWeight: responsive.fontWeight.bold,
            }}
          >
            {l10n.contentUsersLiked.noLikesYet}
          </div>
        )}

        {likedUsers.length > 0 && (
          <VirtualList
            items={likedUsers}
            renderItem={renderItem}
            getItemKey={(row) => row.userId}
            qa={{ id: 'content-liked-users-grid' }}
            scrollContainer={scrollRef}
            gridProps={{
              // Resolved centrally: inside this sheet surface the hook returns the
              // fixed 3-column count matching Flutter's `gridThreeColumnsCount`.
              columnsCount: gridColumnsCount,
              crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
              mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
              itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
              bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
            }}
            {...(hasMore ? { onReachedEnd: loadMore, reachedEndThreshold: 200 } : {})}
          />
        )}

        {!isFirstLoad && isFetching && hasMore && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: responsive.spacing.lg,
              minHeight: responsive.spacing['3xl'],
            }}
          >
            <ExpauseLoadingIndicator />
          </div>
        )}
      </div>
    </div>
  );
};
